import {
	externalPurchaseAmountAtom,
	type GenerateGatewayUrlParams,
	getPurchaseGatewayUrl,
	preferredFiatCurrencyAtom,
	type PurchaseGatewayParams,
	useAppTheme,
	useFiatCurrenciesQuery,
	useLocale
} from '@capsicum/common';
import { useAtomValue } from 'jotai';
import { v4 as uuidv4 } from 'uuid';

import { getSessionAuthenticationToken } from '../../auth/data-access/api/session';
import { useMe } from '../../auth/hooks/useMe';
import { useContextForGatewayFlowAtom } from '../../marketplace/data-access/atoms/useContextForGatewayFlowAtom';
import { isWindowCloseAtom } from '../data-access/atoms/isWindowClose.atom';
/*
 * This hook is used to generate a proper URL to redirect tio the Gateway
 *
 * @param {PurchaseType} type - The type of buying, FTDP, FTO, ... for the gateway to open the right page
 * @param {string} fanTokenCurrencyId - The currency ID of the fan token the user want ot buy
 * @param {string | undefined} baseUrl - The url of the gateway (platform dependant)
 * @param {number} deviceName - name of the device (platform dependant)
 * @param {string} deviceUid - Uuid of the device (platform dependant)
 *
 * @returns an URL string with proper params to send to gateway
 */
export const useGenerateWebLinkToGateway = () => {
	const { me } = useMe();
	const userId = me.externalUserId;
	const preferredFiatCurrency = me.preferredFiatCurrencyCode;
	const fiatCurrency = useAtomValue(preferredFiatCurrencyAtom);

	const { data } = useFiatCurrenciesQuery({ options: { enabled: !fiatCurrency?.id } });
	const externalPurchaseAmount = useAtomValue(externalPurchaseAmountAtom);
	const isWindowClose = useAtomValue(isWindowCloseAtom);
	const { locale } = useLocale();
	const { mode } = useAppTheme();
	const { contextForGateway } = useContextForGatewayFlowAtom();

	const generateWebLinkToGateway = async ({
		baseUrl,
		deviceName,
		deviceUid,
		fanTokenCurrencyId,
		fanTokenOfferingId,
		fanTokenShortCode,
		fiatAmount,
		isWebView,
		paymentCurrencyId,
		processStartedAt,
		purchaseAmount,
		purchaseType
	}: GenerateGatewayUrlParams) => {
		const token = await getSessionAuthenticationToken();

		let selectedPaymentCurrencyId = paymentCurrencyId || fiatCurrency?.id;

		if (!paymentCurrencyId && !fiatCurrency?.id) {
			const currency = data?.find(currency => currency.shortCode === preferredFiatCurrency);

			selectedPaymentCurrencyId = currency?.id;
		}

		const commonParams: PurchaseGatewayParams = {
			baseUrl,
			// Context for TopUp and FanTokenPurchase flow, if it's set it will be used to redirect to it after the TopUp and FanTokenPurchase flow
			context: contextForGateway,
			deviceName: deviceName || navigator.userAgent,
			deviceUid: deviceUid || uuidv4(),
			fanTokenCurrencyId: fanTokenCurrencyId || '',
			fanTokenOfferingId,
			fanTokenShortCode,
			fiatAmount,
			generatedCode: token.code,
			isWebView: isWebView,
			isWindowClose,
			locale,
			paymentCurrencyId: selectedPaymentCurrencyId || '',
			processStartedAt,
			// purchaseAmount is set by actions in the app,
			// externalPurchaseAmount?.purchaseQty can be use to set the amount from a url searchParam
			purchaseAmount: purchaseAmount || externalPurchaseAmount?.purchaseQty || '1',
			purchaseType,
			themeMode: mode,
			userId
		} satisfies PurchaseGatewayParams;

		return getPurchaseGatewayUrl(commonParams);
	};

	return { generateWebLinkToGateway };
};
